/**
 * Cookie Consent Hook
 * Manages user cookie preferences for GDPR compliance
 */

import { useState, useEffect, useCallback } from 'react';

export type CookieConsentStatus = 'pending' | 'accepted' | 'essential-only';

export interface CookiePreferences {
  essential: boolean; // Always true - required for app to function
  functional: boolean;
  analytics: boolean;
}

const COOKIE_CONSENT_KEY = 'trainerpulse-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'trainerpulse-cookie-preferences';

/**
 * Get the current consent status from localStorage
 */
export function getConsentStatus(): CookieConsentStatus {
  if (typeof window === 'undefined') return 'pending';
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (stored === 'accepted' || stored === 'essential-only') {
    return stored;
  }
  return 'pending';
}

/**
 * Get detailed cookie preferences
 */
export function getCookiePreferences(): CookiePreferences {
  if (typeof window === 'undefined') {
    return { essential: true, functional: false, analytics: false };
  }

  const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // Invalid JSON, return defaults
    }
  }

  // Check legacy consent status
  const status = getConsentStatus();
  if (status === 'accepted') {
    return { essential: true, functional: true, analytics: true };
  }
  if (status === 'essential-only') {
    return { essential: true, functional: false, analytics: false };
  }

  return { essential: true, functional: false, analytics: false };
}

/**
 * Check if analytics cookies are allowed
 */
export function isAnalyticsAllowed(): boolean {
  return getCookiePreferences().analytics;
}

/**
 * Check if functional cookies are allowed
 */
export function isFunctionalAllowed(): boolean {
  return getCookiePreferences().functional;
}

/**
 * Hook for managing cookie consent
 */
export function useCookieConsent() {
  const [status, setStatus] = useState<CookieConsentStatus>('pending');
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    functional: false,
    analytics: false,
  });
  const [isLoaded, setIsLoaded] = useState(false);

  // Load saved preferences on mount
  useEffect(() => {
    const savedStatus = getConsentStatus();
    const savedPreferences = getCookiePreferences();
    setStatus(savedStatus);
    setPreferences(savedPreferences);
    setIsLoaded(true);
  }, []);

  /**
   * Accept all cookies
   */
  const acceptAll = useCallback(() => {
    const newPreferences: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPreferences));

    setStatus('accepted');
    setPreferences(newPreferences);
  }, []);

  /**
   * Accept only essential cookies
   */
  const acceptEssentialOnly = useCallback(() => {
    const newPreferences: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, 'essential-only');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPreferences));

    setStatus('essential-only');
    setPreferences(newPreferences);

    // Clear any existing analytics cookies
    clearAnalyticsCookies();
  }, []);

  /**
   * Save custom preferences
   */
  const savePreferences = useCallback((newPreferences: Partial<CookiePreferences>) => {
    const updated: CookiePreferences = {
      essential: true, // Always true
      functional: newPreferences.functional ?? preferences.functional,
      analytics: newPreferences.analytics ?? preferences.analytics,
    };

    const newStatus = updated.analytics || updated.functional ? 'accepted' : 'essential-only';

    localStorage.setItem(COOKIE_CONSENT_KEY, newStatus);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(updated));

    setStatus(newStatus);
    setPreferences(updated);

    // Clear analytics cookies if disabled
    if (!updated.analytics) {
      clearAnalyticsCookies();
    }
  }, [preferences]);

  /**
   * Reset consent (for testing or user request)
   */
  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    setStatus('pending');
    setPreferences({ essential: true, functional: false, analytics: false });
  }, []);

  return {
    status,
    preferences,
    isLoaded,
    isPending: status === 'pending',
    hasConsented: status !== 'pending',
    acceptAll,
    acceptEssentialOnly,
    savePreferences,
    resetConsent,
  };
}

/**
 * Clear analytics-related cookies
 */
function clearAnalyticsCookies() {
  // Clear Google Analytics cookies
  const gaCookies = ['_ga', '_gid', '_gat'];
  gaCookies.forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

  // Clear PostHog cookies
  const phCookies = ['ph_phc_'];
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (phCookies.some(prefix => name.startsWith(prefix))) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
}
